import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import BottomNav from '../BottomNav';
import { useAuth } from '../AuthContext';
import { useTheme } from '../../ThemeContext';

const PAGE_SIZE = 30;
const AUTO_LOAD_THRESHOLD_PX = 420;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_CHARS = 2;

const appendUniqueById = (prevItems, nextItems) => {
  const seenIds = new Set(prevItems.map((item) => Number(item?.id || 0)));
  const uniqueNext = nextItems.filter((item) => {
    const id = Number(item?.id || 0);
    if (!id || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
  return [...prevItems, ...uniqueNext];
};

const MarketplacePage = () => {
  const { theme } = useTheme();
  const { getMarketItems } = useAuth();
  const isDark = theme === 'dark';
  const themeClass = isDark ? 'bg-dark text-white' : 'bg-light text-dark';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [errorText, setErrorText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [priceMinText, setPriceMinText] = useState('');
  const [priceMaxText, setPriceMaxText] = useState('');
  const [filters, setFilters] = useState({ q: '', priceMin: '', priceMax: '' });
  const [filterError, setFilterError] = useState('');
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const scrollRafRef = useRef(null);
  const requestSeqRef = useRef(0);
  const hasActiveFilters = Boolean(filters.q || filters.priceMin || filters.priceMax);
  const hasDraftFilters = Boolean(searchText.trim() || priceMinText.trim() || priceMaxText.trim());

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const rawQuery = searchText.trim();
      const rawPriceMin = priceMinText.trim();
      const rawPriceMax = priceMaxText.trim();
      const priceMinValue = rawPriceMin ? Number(rawPriceMin) : null;
      const priceMaxValue = rawPriceMax ? Number(rawPriceMax) : null;

      if (
        (rawPriceMin && (!Number.isFinite(priceMinValue) || priceMinValue < 0))
        || (rawPriceMax && (!Number.isFinite(priceMaxValue) || priceMaxValue < 0))
      ) {
        setFilterError('Цена должна быть положительным числом');
        return;
      }

      if (
        priceMinValue !== null
        && priceMaxValue !== null
        && priceMinValue > priceMaxValue
      ) {
        setFilterError('Цена от не может быть больше цены до');
        return;
      }

      const nextFilters = {
        q: rawQuery.length >= MIN_SEARCH_CHARS ? rawQuery : '',
        priceMin: rawPriceMin,
        priceMax: rawPriceMax,
      };

      if (rawQuery && rawQuery.length < MIN_SEARCH_CHARS) {
        setFilterError('Для поиска по названию введите минимум 2 символа');
      } else {
        setFilterError('');
      }

      setFilters((prev) => (
        prev.q === nextFilters.q
        && prev.priceMin === nextFilters.priceMin
        && prev.priceMax === nextFilters.priceMax
          ? prev
          : nextFilters
      ));
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [priceMaxText, priceMinText, searchText]);

  const resetFilters = useCallback(() => {
    setSearchText('');
    setPriceMinText('');
    setPriceMaxText('');
    setFilterError('');
    setFilters({ q: '', priceMin: '', priceMax: '' });
  }, []);

  const loadPage = useCallback(async (beforeId = null, { includeCount = true } = {}) => {
    const response = await getMarketItems({
      limit: PAGE_SIZE,
      beforeId,
      q: filters.q,
      priceMin: filters.priceMin,
      priceMax: filters.priceMax,
      includeCount,
    });
    const nextItems = Array.isArray(response?.items) ? response.items : [];
    const responseTotalCount = response?.total_count;
    return {
      items: nextItems,
      totalCount: responseTotalCount !== null
        && responseTotalCount !== undefined
        && Number.isFinite(Number(responseTotalCount))
        ? Number(responseTotalCount)
        : null,
    };
  }, [filters.priceMax, filters.priceMin, filters.q, getMarketItems]);

  const loadInitial = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setLoading(true);
    setErrorText('');
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    try {
      const firstPage = await loadPage(null, { includeCount: true });
      if (requestSeq !== requestSeqRef.current) return;
      setItems(firstPage.items);
      setTotalCount(firstPage.totalCount ?? firstPage.items.length);
      setHasMore(firstPage.items.length >= PAGE_SIZE);
    } catch (ex) {
      if (requestSeq !== requestSeqRef.current) return;
      console.error('loadInitial marketplace error', ex);
      setErrorText(ex?.message || 'Не удалось загрузить барахолку');
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [loadPage]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || items.length === 0) return;
    const requestSeq = requestSeqRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const beforeId = items[items.length - 1]?.id;
      const page = await loadPage(beforeId, { includeCount: false });
      if (requestSeq !== requestSeqRef.current) return;
      setItems((prev) => appendUniqueById(prev, page.items));
      if (page.totalCount !== null) setTotalCount(page.totalCount);
      if (page.items.length < PAGE_SIZE) setHasMore(false);
    } catch (ex) {
      console.error('loadMore marketplace error', ex);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, items, loadPage]);

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = scrollRef.current;
      if (!container || loading || loadingMoreRef.current || !hasMore) return;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToBottom <= AUTO_LOAD_THRESHOLD_PX) {
        void loadMore();
      }
    });
  }, [hasMore, loadMore, loading]);

  useEffect(() => () => {
    if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
  }, []);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={`min-vh-100 pb-5 ${themeClass} scrollable marketplace-page-shell market-page-shell ${isDark ? 'market-page-shell--dark' : 'market-page-shell--light'}`}
    >
      <div className={`main-status-glass ${isDark ? 'main-status-glass--dark' : 'main-status-glass--light'}`} aria-hidden="true" />

      <div className="market-page-topbar">
        <div className="market-page-topbar__card">
          <div className="market-page-title-block">
            <span className="market-page-kicker">Товары участников</span>
            <h1>Барахолка</h1>
            <p>Находки, которые можно купить напрямую у пользователей</p>
          </div>
          <div className="market-page-count" aria-label={`Товаров: ${totalCount}`}>
            <span>{totalCount}</span>
            <small>{hasActiveFilters ? 'найдено' : 'товаров'}</small>
          </div>
        </div>
      </div>

      <Container className="market-page-content pb-5">
        <div className="market-page-topbar-spacer" aria-hidden="true" />

        <Form className="market-search-panel mb-3" onSubmit={(event) => event.preventDefault()}>
          <Row className="g-2 align-items-end">
            <Col xs={12} md={6}>
              <Form.Label className="market-search-label">Поиск по названию</Form.Label>
              <Form.Control
                className="market-form-control market-search-control"
                placeholder="Куртка, обувь, техника..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="market-search-label">Цена от</Form.Label>
              <Form.Control
                className="market-form-control market-search-control"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="0"
                value={priceMinText}
                onChange={(event) => setPriceMinText(event.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="market-search-label">Цена до</Form.Label>
              <Form.Control
                className="market-form-control market-search-control"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="∞"
                value={priceMaxText}
                onChange={(event) => setPriceMaxText(event.target.value)}
              />
            </Col>
            <Col xs={12} md={2} className="d-grid">
              <Button
                className="market-glass-btn market-glass-btn--secondary market-search-reset-btn"
                variant="light"
                type="button"
                onClick={resetFilters}
                disabled={!hasDraftFilters && !hasActiveFilters}
              >
                Сбросить
              </Button>
            </Col>
          </Row>
          {filterError ? (
            <div className="market-search-hint market-search-hint--danger">{filterError}</div>
          ) : hasActiveFilters ? (
            <div className="market-search-hint">Показываем товары по выбранным условиям</div>
          ) : null}
        </Form>

        {errorText && (
          <div className="market-glass-alert market-glass-alert--danger mb-3" role="alert">
            {errorText}
          </div>
        )}

        {loading ? (
          <div className="market-loader"><Spinner animation="border" /></div>
        ) : (
          <>
            <Row className="g-3">
              {items.map((item) => (
                <Col xs={6} md={4} xl={3} key={item.id}>
                  <Card
                    className="h-100 market-item-card"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="market-item-photo-wrap">
                      {item.photo_thumb_url ? (
                        <Card.Img
                          variant="top"
                          src={item.photo_thumb_url}
                          alt={item.title}
                          className="market-item-photo"
                        />
                      ) : (
                        <div className="market-item-photo market-item-photo--empty">
                          Нет фото
                        </div>
                      )}
                    </div>
                    <Card.Body className="market-item-body">
                      <Card.Title className="market-item-title">
                        {item.title}
                      </Card.Title>
                      <div className="market-item-price">
                        {Number(item.price || 0).toLocaleString('ru-RU')} ₽
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              {items.length === 0 && (
                <Col xs={12}>
                  <div className="market-empty-card">
                    {hasActiveFilters ? 'По этим условиям товаров не найдено' : 'В барахолке пока нет товаров'}
                  </div>
                </Col>
              )}
            </Row>

            <div className="d-grid mt-3">
              <Button className="market-glass-btn market-glass-btn--secondary" variant="light" onClick={loadMore} disabled={!hasMore || loadingMore}>
                {loadingMore ? 'Загрузка...' : hasMore ? 'Показать еще' : 'Больше нет товаров'}
              </Button>
            </div>
          </>
        )}
      </Container>

      <Modal
        show={Boolean(selectedItem)}
        onHide={() => setSelectedItem(null)}
        centered
        className={`market-item-modal ${isDark ? 'market-item-modal--dark' : 'market-item-modal--light'}`}
        dialogClassName="market-item-modal-dialog"
        backdropClassName="market-item-modal-backdrop"
        contentClassName={`market-item-modal-content ${isDark ? 'market-item-modal-content--dark' : 'market-item-modal-content--light'}`}
      >
        {selectedItem && (
          <>
            <Modal.Header closeButton className="market-item-modal-header">
              <Modal.Title className="market-item-modal-title">{selectedItem.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="market-item-modal-body">
              <div className="market-item-detail-scroll">
                {selectedItem.photo_url && (
                  <img
                    src={selectedItem.photo_url}
                    alt={selectedItem.title}
                    className="market-item-detail-img"
                  />
                )}
                <div className="market-item-detail-price">{Number(selectedItem.price || 0).toLocaleString('ru-RU')} ₽</div>
                {selectedItem.description ? (
                  <div className="market-item-detail-description">{selectedItem.description}</div>
                ) : (
                  <div className="market-item-detail-empty">Описание не добавлено</div>
                )}
                {selectedItem.seller && (
                  <div className="market-seller-card">
                    <div className="market-seller-info">
                      <span>Продавец</span>
                      <strong>{selectedItem.seller.display_name || `User ${selectedItem.seller.account_id}`}</strong>
                      {selectedItem.seller.chat_handle && <small>@{selectedItem.seller.chat_handle}</small>}
                    </div>
                    {(selectedItem.effective_contact_link || selectedItem.contact_link || selectedItem.seller.contact_link) && (
                      <a
                        className="market-glass-btn market-glass-btn--primary market-seller-contact"
                        href={selectedItem.effective_contact_link || selectedItem.contact_link || selectedItem.seller.contact_link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Связаться
                      </a>
                    )}
                  </div>
                )}
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>

      <BottomNav />
    </div>
  );
};

export default MarketplacePage;
